import type { UnitSystem } from "@/atoms/units";
import { HourlyMarine } from "@/lib/marine";
import { fontStack } from "@/theme";
import { convertHeight, heightUnit } from "@/lib/units";
import { findNearestWind, HourlyWind } from "@/lib/weather";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";
import {
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface ForecastChartProps {
  hourly: HourlyMarine[];
  hourlyWind?: HourlyWind[];
  units: UnitSystem;
  shoreNormal?: number | null;
  selectedIdx?: number | null;
  onSelect?: (idx: number) => void;
}

interface ChartDatum {
  idx: number;
  time: string;
  label: string;
  waveHeight: number;
  windSpeedMph: number | null;
  hour: number;
}

interface DayHeader {
  label: string;
  startIdx: number;
  endIdx: number;
}

/**
 * 5-day forecast chart per DESIGN.md: paper background, single ink swell
 * line, brass/brown wind line, no area fills (tide chart is the exception).
 * Current hour + selected hour use the one accent color only — every
 * other mark is ink or muted ink.
 */
export default function ForecastChart({
  hourly,
  hourlyWind = [],
  units,
  selectedIdx = null,
  onSelect,
}: ForecastChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const inkMuted = theme.palette.ink.muted;
  const ink = theme.palette.ink.main;
  const accent = theme.palette.accent.main;
  const paper = theme.palette.background.default;
  const rule = theme.palette.rule.main;
  const swellColor = theme.palette.chart.swell;
  const windColor = theme.palette.chart.wind;

  const windByTime = new Map(hourlyWind.map((w) => [w.time, w]));
  const hasWindData = hourlyWind.length > 0;

  const chartData: ChartDatum[] = hourly.map((h, i) => {
    const wind = hasWindData
      ? windByTime.get(h.time) ?? findNearestWind(h.time, hourlyWind)
      : null;
    const parsed = parseISO(h.time);
    return {
      idx: i,
      time: h.time,
      label: format(parsed, isMobile ? "HH" : "HH:mm"),
      waveHeight: convertHeight(h.waveHeightM, units),
      windSpeedMph:
        wind != null
          ? units === "imperial"
            ? wind.windSpeedKmh * 0.621371
            : wind.windSpeedKmh
          : null,
      hour: parsed.getHours(),
    };
  });

  const dayHeaders: DayHeader[] = [];
  let currentDay = "";
  chartData.forEach((d, i) => {
    const dayKey = format(parseISO(d.time), "yyyy-MM-dd");
    if (dayKey !== currentDay) {
      if (dayHeaders.length > 0) {
        dayHeaders[dayHeaders.length - 1].endIdx = i - 1;
      }
      dayHeaders.push({
        label: format(parseISO(d.time), "EEE d").toUpperCase(),
        startIdx: i,
        endIdx: chartData.length - 1,
      });
      currentDay = dayKey;
    }
  });

  const nowMs = Date.now();
  let nowIdx: number | null = null;
  let minTimeDiff = Infinity;
  chartData.forEach((d, i) => {
    const diff = Math.abs(parseISO(d.time).getTime() - nowMs);
    if (diff < minTimeDiff) {
      minTimeDiff = diff;
      nowIdx = i;
    }
  });

  if (chartData.length < 2) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography sx={{ color: "ink.muted" }}>Forecast data unavailable.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Unit labels sit with their own series so "ft" reads as the
          left (swell) axis and "mph" as the right (wind) axis. Previous
          version collapsed them into a single "FT · MPH" string on the
          right, which conflated the two scales visually. */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 1,
        }}
      >
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Swell · {heightUnit(units)}
        </Typography>
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Wind · {units === "imperial" ? "mph" : "km/h"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          borderTop: "1px solid",
          borderColor: "divider",
          ml: isMobile ? "30px" : "42px",
          mr: isMobile ? "30px" : "42px",
        }}
      >
        {dayHeaders.map((dh, i) => {
          const span = dh.endIdx - dh.startIdx + 1;
          const total = chartData.length;
          return (
            <Box
              key={i}
              sx={{
                flex: `${span} 0 0`,
                width: `${(span / total) * 100}%`,
                textAlign: "left",
                py: 0.75,
                pl: 0.5,
                borderRight: i < dayHeaders.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Typography variant="overline" sx={{ color: "ink.muted" }}>
                {dh.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: "100%",
          minWidth: 100,
          height: { xs: 220, sm: 280, md: 320 },
          "& .recharts-wrapper, & .recharts-surface, & .recharts-tooltip-wrapper": {
            outline: "none",
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
            onClick={(e) => {
              if (!onSelect) return;
              const raw = e?.activeTooltipIndex;
              const idx = typeof raw === "string" ? Number(raw) : raw;
              if (typeof idx === "number" && idx >= 0 && idx < chartData.length) {
                onSelect(idx);
              }
            }}
            style={onSelect ? { cursor: "pointer" } : undefined}
          >
            <XAxis
              dataKey="idx"
              type="number"
              domain={[0, chartData.length - 1]}
              tick={false}
              stroke={rule}
              height={8}
            />
            <YAxis
              yAxisId="swell"
              tick={{
                fontSize: 11,
                fill: inkMuted,
                fontFamily: fontStack.mono,
              }}
              width={isMobile ? 30 : 42}
              axisLine={false}
              tickLine={false}
              stroke={rule}
            />
            <YAxis
              yAxisId="wind"
              orientation="right"
              tick={{
                fontSize: 11,
                fill: inkMuted,
                fontFamily: fontStack.mono,
              }}
              width={isMobile ? 30 : 42}
              axisLine={false}
              tickLine={false}
              stroke={rule}
            />

            <Tooltip
              cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: "2 3" }}
              contentStyle={{
                backgroundColor: paper,
                border: `1px solid ${rule}`,
                borderRadius: 4,
                fontSize: 12,
                color: ink,
                padding: "8px 10px",
                fontFamily: fontStack.mono,
                boxShadow: "none",
              }}
              labelStyle={{
                fontFamily: fontStack.sans,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: inkMuted,
                marginBottom: 4,
              }}
              itemStyle={{ color: ink }}
              formatter={(value: number, name: string) => {
                if (name === "Swell")
                  return [`${value.toFixed(1)} ${heightUnit(units)}`, name];
                if (name === "Wind")
                  return [
                    `${value.toFixed(0)} ${units === "imperial" ? "mph" : "km/h"}`,
                    name,
                  ];
                return [value, name];
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              labelFormatter={(_: any, payload: any) => {
                const d = payload?.[0]?.payload as ChartDatum | undefined;
                if (!d) return "";
                return format(parseISO(d.time), "EEE HH:mm").toUpperCase();
              }}
            />

            {/* Current-hour accent marker — vertical line + dot on the
                series. Single source of attention per DESIGN.md. */}
            {nowIdx !== null && (
              <ReferenceLine
                yAxisId="swell"
                x={nowIdx}
                stroke={accent}
                strokeWidth={1}
                label={{
                  value: "NOW",
                  position: "top",
                  fill: accent,
                  fontSize: 10,
                  fontFamily: fontStack.sans,
                  letterSpacing: 1,
                }}
              />
            )}
            {nowIdx !== null && (
              <ReferenceDot
                yAxisId="swell"
                x={nowIdx}
                y={chartData[nowIdx]?.waveHeight ?? 0}
                r={3.5}
                fill={accent}
                stroke={paper}
                strokeWidth={1.5}
              />
            )}

            {selectedIdx != null && selectedIdx !== nowIdx && chartData[selectedIdx] && (
              <ReferenceDot
                yAxisId="swell"
                x={selectedIdx}
                y={chartData[selectedIdx].waveHeight}
                r={3.5}
                fill={paper}
                stroke={ink}
                strokeWidth={1.5}
              />
            )}

            <Line
              yAxisId="swell"
              type="monotone"
              dataKey="waveHeight"
              name="Swell"
              stroke={swellColor}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4, fill: accent, stroke: paper, strokeWidth: 1.5 }}
              isAnimationActive={false}
            />
            {hasWindData && (
              <Line
                yAxisId="wind"
                type="monotone"
                dataKey="windSpeedMph"
                name="Wind"
                stroke={windColor}
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: 3,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        <LegendItem color={swellColor} label="Swell" />
        <LegendItem color={windColor} label="Wind" dashed />
        <LegendItem color={accent} label="Now" />
      </Box>
    </Box>
  );
}

function LegendItem({
  color,
  label,
  dashed = false,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 14,
          height: 2,
          backgroundColor: dashed ? "transparent" : color,
          borderTop: dashed ? `1.5px dashed ${color}` : "none",
        }}
      />
      <Typography variant="overline" sx={{ color: "ink.muted" }}>
        {label}
      </Typography>
    </Box>
  );
}
